import { useCallback, useState, useEffect } from 'react';
import { type ContextResolver, useRecommendations } from '@atlassian/app-recommendations-kit';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useSubProduct } from '@atlassian/jira-subproduct';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	COHORT,
	JIRA_1P_APPS_MENU_USE_CASE,
	PACKAGE_NAME,
	RECOMMENDATION_ENTITIES,
	TEAM_NAME,
	type RecommendationEntityIdType,
	type CohortType,
} from '../constants';

export interface useAppRecommendationsProps {
	cohort: CohortType | string;
	sessionId: string;
}

export function useAppRecommendations({ cohort, sessionId }: useAppRecommendationsProps) {
	const [entityId, setEntityId] = useState<RecommendationEntityIdType>();
	const [isRecommendationLoaded, setIsRecommendationLoaded] = useState<boolean>(false);

	const { cloudId } = useTenantContext();
	const subProduct = useSubProduct();

	const contextResolver: ContextResolver = useCallback(
		() => ({
			product: 'jira',
			useCase: JIRA_1P_APPS_MENU_USE_CASE,
			tenantId: cloudId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			subproduct: subProduct as string,
			sessionId: cohort === COHORT.VARIATION ? sessionId : undefined,
		}),
		[cloudId, subProduct, sessionId, cohort],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	useEffect(() => {
		async function getRecos() {
			try {
				const result = await getRecommendations(1, asEntityIdList(RECOMMENDATION_ENTITIES));

				setIsRecommendationLoaded(true);

				const { response: { data } = {} } = result;

				if (data && data.length > 0 && RECOMMENDATION_ENTITIES.includes(data[0].entityId)) {
					setEntityId(data[0].entityId);
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (!error.message && typeof error.reason === 'string') {
					// This is a fix for CB errors as the fireErrorAnalytics requires message property
					error.message = error.reason;
				}

				fireErrorAnalytics({
					meta: {
						id: 'recommendationFetchFailed',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error,
				});
			}
		}
		getRecos();
	}, [getRecommendations]);

	return { entityId, contextResolver, isRecommendationLoaded };
}

const asEntityIdList = (entities: typeof RECOMMENDATION_ENTITIES) =>
	entities.map((entity) => ({ entityId: entity }));
